import { configApi, setBearerToken } from '../../../../shared/utils/http-client.js'
import { ssoLoginApi } from '../../../../shared/utils/api/login.js'
import { COOKIE_MODULE_AREA_FULL_PERMISSIONS, COMPONENTS_MODULE_FULL_PERMISSIONS } from '../../../../shared/permissions/admin-portal-permissions.js'
import router from '../../router/index.js'
import { changeUserBrandIds, resetBrands } from '../../../../shared/state/brands.js'
import { parseToken } from '../../../../shared/utils/parse-token.js'
import { decideSessionTick } from '../../../../shared/utils/session-sync.js'
import { SSO_ERROR, SESSION_EXPIRED } from '../../router/route-names.js'
import { clearNavbarItems } from '../../../../shared/state/admin-portal-navigation.js'

const COOKIE_MODULE_AREA_PERMISSIONIDS = [1, 2, 3, 4, 5, 6, 7, 8, 9]
const COMPONENTS_AREA_PERMISSIONIDS = [52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 70]

export const state = {
	currentUser: getSavedState('auth.currentUser'),
	userDetails: {},
	migrationUrl: null,
	isSSO: false,
	sessionWarning: false,
	countdown: 0,
	isRefreshingToken: false
}

const getCookieValue = name =>
	document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || ''

let sessionCheckInterval

export const mutations = {
	SET_CURRENT_USER (state, newValue) {
		state.currentUser = newValue
		saveState('auth.currentUser', newValue)
		if (newValue == null) {
			state.userDetails = {}
		}
	},
	SET_USER_DETAILS (state, userDetails) {
		state.userDetails = userDetails
		changeUserBrandIds(userDetails.brandIds)
	},
	SET_MIGRATION_URL (state, migrationUrl) {
		state.migrationUrl = migrationUrl
	},
	SET_SESSION_WARNING (state, warning) {
		state.sessionWarning = warning
	},
	SET_COUNTDOWN (state, countdown) {
		state.countdown = countdown
	},
	SET_INSUFFICIENT_ACCESS (state, value) {
		state.insufficientAccess = value
	},
	SET_INSUFFICIENT_ACCESS_REASON (state, reason) {
		state.insufficientAccessReason = reason
	},
	SET_IS_REFRESHING_TOKEN (state, value) {
		state.isRefreshingToken = value
	}
}

export const getters = {
	// Whether the user is currently logged in.
	loggedIn (state) {
		return !!state.currentUser?.userId
	},
	userPermissionsRetrieved (state) {
		return !!state.userDetails?.productPermissions
	},
	userFullName (state) {
		return state.userDetails?.fullName
	},
	userId (state) {
		return state.currentUser?.userId
	},
	userDefaultDashboardId (state) {
		return state.userDetails?.defaultDashboardId
	},
	migrationUrl (state) {
		return state.migrationUrl
	},
	productAreaPermission: state => permissionData =>
		checkUserHasPermission(state, permissionData),
	tokenInDate (state) {
		if (!state.currentUser?.userId) {
			return false
		}
		const expiry = new Date(0)
		expiry.setUTCSeconds(state.currentUser.tokenExpiry)
		const now = new Date()
		return (expiry > now)
	},
	userHasUniversalBrand (state) {
		return state.userDetails?.brandIds.includes(0)
	},
	userBrands (state) {
		const brands = []
		brands.push(...state.userDetails?.brandIds)
		return brands
	},
	sessionCookieExists () {
		const token = getCookieValue('admin-portal-token')
		return !!token
	},
	loggedInWithSSO (state) {
		return state.currentUser?.isSSO
	},
	sessionWarning (state) {
		return state.sessionWarning
	},
	countdown (state) {
		return state.countdown
	},
	hasInsufficientAccess (state) {
		return state.insufficientAccess
	},
	insufficientAccessReason (state) {
		return state.insufficientAccessReason
	}
}

export const actions = {
	setJwtTokenFromString ({ commit, dispatch }, jwtString) {
		if (jwtString) {
			const parsedToken = parseToken(jwtString)
			const jwtTokenExpiry = parsedToken.exp
			const jwtUserId = parsedToken.userId
			const user = {
				tokenExpiry: jwtTokenExpiry,
				userId: jwtUserId
			}
			setBearerToken(jwtString)
			setTokenCookie(jwtString)
			commit('SET_CURRENT_USER', user)
			dispatch('startSessionCheck')
			return Promise.resolve(null)
		}
	},
	checkForExistingSession ({ commit, getters, dispatch }) {
		if (getters.sessionCookieExists) {
			const token = getCookieValue('admin-portal-token')
			if (token != null && token !== '') {
				setBearerToken(token)
				const parsedToken = parseToken(token)
				const user = {
					userId: getters.userId,
					tokenExpiry: parsedToken.exp
				}
				commit('SET_CURRENT_USER', user)
				dispatch('startSessionCheck')
			}
		}
	},
	ssoLogIn ({ dispatch }, { identityClaimTypeName, stateGuid, productId }) {
		return ssoLoginApi({ identityClaimTypeName, stateGuid, productId }).then(({ data }) => dispatch('handleSSOLogin', { data: data, isSSO: true }).then)
	},
	// Logs in the current user.
	logIn ({ commit, dispatch, getters }, { username, password } = {}) {
		if (getters.loggedIn) return dispatch('validate')
		return configApi
			.post('/api/authorize/adminportaluser', { emailAddress: username, password })
			.then(response => {
				// 202 response code indicates OTP login is required
				if (response.status === 202) {
					localStorage.setItem('mfaGuid', response.data.mfaGuid)
					return {
						otpLoginRequired: true
					}
				} else {
					const parsedToken = parseToken(response.data.token)
					const user = {
						tokenExpiry: parsedToken.exp,
						userId: parsedToken.userId,
						otpLoginRequired: false
					}
					commit('SET_CURRENT_USER', user)
					setBearerToken(response.data.token)
					setTokenCookie(response.data.token)
					dispatch('startSessionCheck')
					return user
				}
			})
	},
	otpLogIn ({ commit, dispatch, getters }, { otpCode, mfaGuid } = {}) {
		if (getters.loggedIn) return dispatch('validate')
		clearNavbarItems()
		return configApi
			.post('/api/Authorize/LoginOtp', { loginCode: otpCode, mfaGuid })
			.then(response => {
				const parsedToken = parseToken(response.data.token)
				const user = {
					tokenExpiry: parsedToken.exp,
					userId: parsedToken.userId,
					oneTimePass: response.oneTimePass
				}
				commit('SET_CURRENT_USER', user)
				setBearerToken(response.data.token)
				setTokenCookie(response.data.token)
				dispatch('startSessionCheck')
				return user
			})
	},
	// SSO login
	async handleSSOLogin ({ commit, dispatch, getters }, { data }) {
		if (getters.loggedIn) return dispatch('validate')
		setBearerToken(data.token)
		setTokenCookie(data.token)
		const parsedToken = parseToken(data.token)
		const user = {
			tokenExpiry: parsedToken.exp,
			userId: parsedToken.userId,
			isSSO: true
		}
		commit('SET_CURRENT_USER', user)
		dispatch('startSessionCheck')
		try {
			await dispatch('getUserDetails')
		} catch (error) {
			// If getUserDetails throws INSUFFICIENT_ACCESS error,
			// it will already have handled the redirect to no-access page
			if (error.message === 'INSUFFICIENT_ACCESS') {
				return Promise.reject(error)
			}
			throw error
		}
	},
	// Get sensitive user details.
	getUserDetails ({ commit, dispatch, getters } = {}) {
		if (getters.loggedIn && getters.tokenInDate) {
			return configApi
				.get('/api/adminportalusers/getcurrentuserdetails')
				.then(({ data }) => {
					const userDetails = {
						fullName: data.currentUserDetails.userPreview.fullName,
						defaultPage: data.currentUserDetails.userPreview.defaultPage,
						brandIds: data.currentUserDetails.brandIds,
						defaultDashboardId: data.currentUserDetails.user.defaultDashboardId,
						productPermissions: data.currentUserDetails.permissionIds
					}

					// Check if user has no brands or no permissions
					const hasNoBrands = !userDetails.brandIds || userDetails.brandIds.length === 0
					const hasNoPermissions = !userDetails.productPermissions || userDetails.productPermissions.length === 0
					if (hasNoBrands || hasNoPermissions) {
						// Determine what's missing
						let reason = 'both'
						if (hasNoBrands && !hasNoPermissions) {
							reason = 'brands'
						} else if (!hasNoBrands && hasNoPermissions) {
							reason = 'permissions'
						}
						// Set flag FIRST before any other actions
						commit('SET_INSUFFICIENT_ACCESS', true)
						commit('SET_INSUFFICIENT_ACCESS_REASON', reason)
						commit('SET_CURRENT_USER', null)
						clearClientSessionArtifacts()

						// Return rejected promise
						return Promise.reject(new Error('INSUFFICIENT_ACCESS'))
					}

					commit('SET_USER_DETAILS', userDetails)
					resetBrands()
					return userDetails
				})
				.catch(error => {
					console.warn(error)

					if (error.message === 'INSUFFICIENT_ACCESS') {
						return Promise.reject(error)
					}

					commit('SET_CURRENT_USER', null)
					dispatch('logOut', SESSION_EXPIRED)
					return Promise.resolve(null)
				})
		} else {
			commit('SET_CURRENT_USER', null)
			dispatch('logOut', SESSION_EXPIRED)
			return Promise.resolve(null)
		}
	},
	getLegacyUrl ({ commit, getters } = {}) {
		if (getters.migrationUrl != null) {
			return getters.migrationUrl
		}
		return configApi
			.get('/api/LegacyUrls')
			.then(({ data }) => {
				const migrationUrl = []
				migrationUrl.push(data.useVueAdminPortalLoginPage)
				migrationUrl.push(data.legacyAdminPortalUrl)
				commit('SET_MIGRATION_URL', migrationUrl)
				return migrationUrl
			})
			.catch(error => {
				console.error(error)
				return Promise.resolve(null)
			})
	},
	// Logs out the current user.
	logOut ({ getters, dispatch }, routeName) {
		clearInterval(sessionCheckInterval)
		if (routeName === SESSION_EXPIRED) {
			dispatch('handleLogout', routeName)
			return
		}

		if (getters.loggedIn && getters.tokenInDate) {
			return configApi.post('/api/authorize/adminportaluserlogout', { revokeAllSessions: false })
				.finally(() => {
					dispatch('handleLogout', routeName)
				})
		} else {
			dispatch('handleLogout', routeName)
		}
	},
	handleLogout ({ getters, commit }, routeName) {
		commit('SET_CURRENT_USER', null)
		commit('SET_SESSION_WARNING', false)
		clearClientSessionArtifacts()

		// If the current route is Session Expired, do not proceed with redirects or further API calls
		if (routeName === SESSION_EXPIRED) {
			return
		}

		const migrationUrl = getters.migrationUrl
		const tenantGuid = sessionStorage.getItem('tenantGuid')
		const authHandler = sessionStorage.getItem('authHandler')
		const migjwt = sessionStorage.getItem('migjwt')
		const isSSO = sessionStorage.getItem('isSSO')

		if (isSSO != null && tenantGuid != null && authHandler != null) {
			sessionStorage.removeItem('tenantGuid')
			sessionStorage.removeItem('authHandler')
			sessionStorage.removeItem('isSSO')
			if (routeName === SSO_ERROR) {
				router.push({ name: SSO_ERROR })
				return
			}
			window.location.href = `${authHandler}/signOut/${tenantGuid}`
		} else if (migrationUrl != null && migjwt === 'true') {
			sessionStorage.removeItem('migjwt')
			location.href = 'https://' + migrationUrl[1] + '/Home/Logout'
		} else {
			router.push({ path: '/login' })
		}
	},
	// Validates the current user's token and refreshes it
	// with new data from the API.
	validate ({ commit, dispatch, state, getters }) {
		if (!state.currentUser) return Promise.resolve(null)
		const expiry = new Date(0)
		expiry.setUTCSeconds(state.currentUser.tokenExpiry)
		const now = new Date()
		const closeToExpire = new Date(expiry - 1500000)
		const tokenInDateAndNotAboutToExpire = (now < closeToExpire)
		if (getters.loggedIn && tokenInDateAndNotAboutToExpire) {
			return true
		}
		// Read cookie and ensure currentUser is populated and bearer token is set
		const token = getCookieValue('admin-portal-token')
		if (!token) {
			commit('SET_CURRENT_USER', null)
			dispatch('logOut', SESSION_EXPIRED)
			return Promise.resolve(null)
		}
		setBearerToken(token)
		const parsedToken = parseToken(token)
		const user = {
			userId: getters.userId,
			tokenExpiry: parsedToken.exp
		}
		commit('SET_CURRENT_USER', user)
		// Check token expiry date
		expiry.setUTCSeconds(state.currentUser.tokenExpiry)
		if (now > expiry) {
			commit('SET_CURRENT_USER', null)
			dispatch('logOut', SESSION_EXPIRED)
			return Promise.resolve(null)
		}
		// Only call refresh token when there are 5 minutes or fewer before expiry
		return configApi
			.get('/api/authorize/adminportaluserrefreshtoken')
			.then(response => {
				const newParsedToken = parseToken(response.data.token)
				const tokenViewModel = { token: response.data.token, tokenExpiry: newParsedToken.exp }
				const newUser = {
					userId: getters.userId,
					tokenExpiry: newParsedToken.exp
				}
				setBearerToken(response.data.token)
				setTokenCookie(response.data.token)
				commit('SET_CURRENT_USER', newUser)
				commit('SET_SESSION_WARNING', false)
				dispatch('startSessionCheck')
				return tokenViewModel
			})
			.catch(error => {
				if (error.response && error.response.status === 401) {
					commit('SET_CURRENT_USER', null)
				} else {
					console.warn(error)
				}
				return Promise.resolve(null)
			})
	},
	startSessionCheck ({ dispatch }) {
		clearInterval(sessionCheckInterval)
		// Poll once a second and reconcile against the SHARED token cookie (see
		// checkSession). The per-second cadence also drives the warning countdown.
		sessionCheckInterval = setInterval(() => dispatch('checkSession'), 1000)
	},
	// Reconcile this tab against the shared token cookie, then drive the warning /
	// expiry. Runs on the session-check interval and on tab re-focus (AB#26125).
	// Using the cookie (not a stale in-memory expiry) means activity in any tab
	// keeps every tab alive, and a background tab can't tear down a session that
	// another tab has just refreshed.
	checkSession ({ state, commit, dispatch }) {
		// Session torn down between ticks (logout, /sso/logout guard) -> stop.
		if (!state.currentUser) {
			clearInterval(sessionCheckInterval)
			return
		}
		const cookieToken = getCookieValue('admin-portal-token')
		const result = decideSessionTick({
			cookieToken,
			knownExpirySeconds: state.currentUser.tokenExpiry,
			nowMs: Date.now()
		})
		// Cookie gone (logged out in another tab) or genuinely expired.
		if (result.kind === 'gone' || result.kind === 'expired') {
			dispatch('sessionExpired')
			return
		}
		// If the shared cookie now belongs to a DIFFERENT user (someone logged in on
		// another tab), re-initialise this tab from the cookie rather than running
		// under the new user's token while showing the previous user's cached
		// permissions. Checked independently of `adopt`: JWT `exp` has 1-second
		// granularity, so a same-second cross-user login can leave `exp` unchanged.
		if (result.userId != null && result.userId !== state.currentUser.userId) {
			window.location.reload()
			return
		}
		// Same user, token refreshed in another tab: adopt it in place.
		if (result.adopt) {
			setBearerToken(cookieToken)
			commit('SET_CURRENT_USER', { ...state.currentUser, tokenExpiry: result.expirySeconds })
		}
		if (result.kind === 'warning') {
			commit('SET_SESSION_WARNING', true)
			commit('SET_COUNTDOWN', Math.max(0, Math.floor(result.timeLeftMs / 1000)))
		} else if (state.sessionWarning) {
			commit('SET_SESSION_WARNING', false)
		}
	},
	sessionExpired ({ commit, dispatch }) {
		commit('SET_CURRENT_USER', null)
		commit('SET_SESSION_WARNING', false)
		dispatch('handleLogout', SESSION_EXPIRED)

		router.push({ name: SESSION_EXPIRED })
	},
	// Called when there's API activity to indicate user is active
	// This proactively refreshes the token if approaching expiry
	recordActivity ({ state, commit, dispatch }) {
		// Only proceed if user is logged in and not already refreshing
		if (!state.currentUser || state.isRefreshingToken) return

		const expiry = new Date(0)
		expiry.setUTCSeconds(state.currentUser.tokenExpiry)
		const now = new Date()
		const timeLeft = expiry - now

		// If we're within 25 minutes of expiry, proactively refresh the token
		// This prevents the session warning from appearing during active use
		const REFRESH_THRESHOLD = 1500000 // 25 minutes in milliseconds
		if (timeLeft <= REFRESH_THRESHOLD && timeLeft > 0) {
			// Clear any existing warning first
			if (state.sessionWarning) {
				commit('SET_SESSION_WARNING', false)
			}
			// Set flag to prevent concurrent refreshes
			commit('SET_IS_REFRESHING_TOKEN', true)
			// Refresh the token to extend the session
			dispatch('validate').finally(() => {
				commit('SET_IS_REFRESHING_TOKEN', false)
			})
		} else if (state.sessionWarning) {
			// If there's a warning showing but we're not close to expiry, clear it
			commit('SET_SESSION_WARNING', false)
		}
	}
}

// ===
// Private helpers
// ===

function getSavedState (key) {
	return JSON.parse(window.localStorage.getItem(key))
}

function saveState (key, state) {
	window.localStorage.setItem(key, JSON.stringify(state))
}

export function clearClientSessionArtifacts () {
	clearInterval(sessionCheckInterval)
	try { sessionStorage.removeItem('saw-splash') } catch {}
	removeCookie('admin-portal-token')
	removeCookie('ASP.NET_SessionId')
	localStorage.removeItem('vueform-builder')
	localStorage.removeItem('vueform-dark-mode')
	localStorage.removeItem('vueform-element-closed')
	localStorage.removeItem('vueform-history')
	localStorage.removeItem('vueform-themes')
	clearNavbarItems()
}

function setTokenCookie (token) {
	if (!token) {
		removeCookie('admin-portal-token')
	} else {
		const parsedToken = parseToken(token)
		const expiry = new Date(0)
		expiry.setUTCSeconds(parsedToken.exp)
		document.cookie =
    'admin-portal-token=' + token + '; expires=' + expiry.toUTCString() + '; path=/;'
	}
}

function checkUserHasPermission (state, permissionId) {
	// Check if permissionId is an array
	if (Array.isArray(permissionId)) {
		// If any of the permission IDs pass the check, return true
		return permissionId.some(id => checkSinglePermission(state, id))
	}
	// Otherwise, check a single permission ID
	return checkSinglePermission(state, permissionId)
}

function checkSinglePermission (state, permissionId) {
	if (state.userDetails?.productPermissions?.includes(COOKIE_MODULE_AREA_FULL_PERMISSIONS) && (COOKIE_MODULE_AREA_PERMISSIONIDS.includes(permissionId))) return true
	if (state.userDetails?.productPermissions?.includes(COMPONENTS_MODULE_FULL_PERMISSIONS) && (COMPONENTS_AREA_PERMISSIONIDS.includes(permissionId))) return true
	return state.userDetails?.productPermissions?.includes(
		permissionId
	)
}

export function removeCookie (cookieName) {
	if (cookieName) {
		document.cookie = cookieName.trim() + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
	}
}
